import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiMail, FiCheck, FiSend } from 'react-icons/fi'

const PricingGuide: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  // Automatically get current year
  const currentYear = new Date().getFullYear()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!email) {
      setError('Please enter your email address')
      return
    }

    if (!/^\S+@\S+$/i.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)
    
    // Simulate API call to SendGrid
    try {
      // TODO: Replace with actual SendGrid API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // In production, this would be:
      // await sendPricingGuideEmail(email)
      
      console.log('Pricing guide email sent to:', email)
      setIsSubmitted(true)
    } catch (err) {
      setError('Failed to send email. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="pricing" className="section-padding bg-gradient-to-br from-neutral-50 to-neutral-100">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="max-w-2xl mx-auto px-4">
            <h2 className="font-bold text-neutral-800 mb-4 sm:mb-6" style={{ fontSize: 'clamp(28px, 4vw, 48px)' }}>
              Get Our <span className="gradient-text">{currentYear} Pricing Guide</span>
            </h2>
            <p className="body-longform text-neutral-600 mb-6 sm:mb-8 text-base sm:text-lg leading-relaxed">
              Enter your email and we'll send you our complete pricing information for all booking services, 
              special packages, and venue partnership rates.
            </p>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card p-6 sm:p-8 text-center"
              >
                <div className="p-3 sm:p-4 bg-green-100 rounded-full text-green-600 inline-block mb-3 sm:mb-4">
                  <FiCheck size={28} className="sm:w-8 sm:h-8" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-neutral-800 mb-2">
                  Pricing Guide Sent!
                </h3>
                <p className="text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
                  Check your email for the complete pricing guide. It should arrive within a few minutes.
                </p>
                <button
                  onClick={() => {
                    setIsSubmitted(false)
                    setEmail('')
                  }}
                  className="text-primary hover:text-secondary transition-colors duration-200 font-medium text-sm sm:text-base min-h-[44px] flex items-center justify-center mx-auto px-4"
                >
                  Send to Another Email
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="card p-6 sm:p-8"
              >
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-2">
                      Email Address
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="flex-1">
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 min-h-[44px] text-base"
                          placeholder="your@email.com"
                          disabled={isSubmitting}
                        />
                      </div>
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                        whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                        className="btn-primary flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="loading-spinner"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <FiSend size={18} className="sm:w-5 sm:h-5" />
                            <span className="whitespace-nowrap">Send Guide</span>
                          </>
                        )}
                      </motion.button>
                    </div>
                    {error && (
                      <p className="mt-2 text-xs sm:text-sm text-red-600">{error}</p>
                    )}
                  </div>
                  
                  <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed">
                    <FiMail className="inline mr-1" size={14} />
                    We'll email you the pricing guide and keep you updated on our latest rates and availability.
                  </p>
                </form>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default PricingGuide